import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';

interface EventRow {
  school_event: string; ended: string; attendees: number; ticket_rev: number;
  post_7d: number; post_30d: number; post_60d: number;
  clubs_7d: number; clubs_60d: number; club_buyers_7d: number; club_buyers_60d: number;
}
interface DetailRow {
  product: string; buyers: number; qty: number;
  rev_7d: number; rev_30d: number; rev_60d: number;
}

const fmt = (v: number) => '$' + Math.round(v).toLocaleString();

export default function PostSchoolSales() {
  const [since, setSince] = useState('2026-01-01');
  const [until, setUntil] = useState('');
  const [rows, setRows] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<EventRow | null>(null);
  const [detail, setDetail] = useState<DetailRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = async (row: EventRow) => {
    setSelected(row); setDetail([]); setDetailLoading(true);
    try {
      const r = await fetch(`${API_BASE}/financial-reports/post-school/detail?ended=${row.ended}`);
      const j = await r.json();
      if (j.success) setDetail(j.data || []);
    } catch { /* modal shows empty state */ }
    setDetailLoading(false);
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/financial-reports/post-school?since=${since}${until ? `&until=${until}` : ''}`);
      const j = await r.json();
      if (!j.success) throw new Error(j.error);
      setRows(j.data || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [since, until]);

  useEffect(() => { load(); }, [load]);

  const tot = rows.reduce((a, r) => ({
    att: a.att + Number(r.attendees), p7: a.p7 + Number(r.post_7d),
    p30: a.p30 + Number(r.post_30d), p60: a.p60 + Number(r.post_60d),
    cb60: a.cb60 + Number(r.club_buyers_60d),
  }), { att: 0, p7: 0, p30: 0, p60: 0, cb60: 0 });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Post-School Sales</h1>
          <p className="text-sm text-gray-500">
            Attendee purchases in the 7 / 30 / 60 days after each school event (anchored to actual event end date).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={since} onChange={e => setSince(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <span className="text-gray-400">→</span>
          <input type="date" value={until} onChange={e => setUntil(e.target.value)} className="border rounded px-2 py-1 text-sm" placeholder="today" />
          <button onClick={load} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          ['Attendees', tot.att.toLocaleString()],
          ['+7 day sales', fmt(tot.p7)],
          ['+60 day sales', fmt(tot.p60)],
          ['Captured in week 1', tot.p60 ? Math.round(100 * tot.p7 / tot.p60) + '%' : '—'],
          ['Club attach (60d)', tot.att ? Math.round(100 * tot.cb60 / tot.att) + '%' : '—'],
        ].map(([label, v]) => (
          <div key={label as string} className="bg-white rounded-lg shadow p-4">
            <div className="text-xs uppercase text-gray-500">{label}</div>
            <div className="text-xl font-bold text-gray-900">{v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Event', 'Ended', 'Att', 'Ticket rev', '+7d', '+30d', '+60d', '% in 7d', '7d/att', 'Clubs 60d', 'Club buyers 7d→60d'].map(h => (
                <th key={h} className="px-3 py-2 text-right first:text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => {
              const pctIn7 = Number(r.post_60d) > 0 ? Math.round(100 * Number(r.post_7d) / Number(r.post_60d)) : 0;
              const low = pctIn7 < 60 || Number(r.post_7d) / Math.max(1, Number(r.attendees)) < 800;
              return (
                <tr key={r.school_event} onClick={() => openDetail(r)}
                    className={`cursor-pointer hover:bg-blue-50 ${Number(r.post_60d) < 500 ? 'bg-red-50' : low ? 'bg-yellow-50' : ''}`}>
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{r.school_event}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{r.ended}</td>
                  <td className="px-3 py-2 text-right">{r.attendees}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{fmt(Number(r.ticket_rev))}</td>
                  <td className="px-3 py-2 text-right">{fmt(Number(r.post_7d))}</td>
                  <td className="px-3 py-2 text-right">{fmt(Number(r.post_30d))}</td>
                  <td className="px-3 py-2 text-right font-medium">{fmt(Number(r.post_60d))}</td>
                  <td className="px-3 py-2 text-right">{pctIn7}%</td>
                  <td className="px-3 py-2 text-right">{fmt(Number(r.post_7d) / Math.max(1, Number(r.attendees)))}</td>
                  <td className="px-3 py-2 text-right">{fmt(Number(r.clubs_60d))}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{r.club_buyers_7d} → {r.club_buyers_60d}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{selected.school_event}</h2>
                <p className="text-sm text-gray-500">
                  Ended {selected.ended} · {selected.attendees} attendees · what they bought in the 60 days after
                </p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-y-auto p-4">
              {detailLoading ? (
                <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
              ) : detail.length === 0 ? (
                <p className="text-sm text-gray-400 py-8 text-center">No post-event purchases found.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b">
                      <th className="text-left py-2 pr-2">Product</th>
                      <th className="text-right py-2 px-2">Buyers</th>
                      <th className="text-right py-2 px-2">Qty</th>
                      <th className="text-right py-2 px-2">+7d</th>
                      <th className="text-right py-2 px-2">+30d</th>
                      <th className="text-right py-2 pl-2">+60d</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.map((d, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-1.5 pr-2">{d.product}</td>
                        <td className="text-right py-1.5 px-2 text-gray-500">{d.buyers}</td>
                        <td className="text-right py-1.5 px-2 text-gray-500">{Number(d.qty)}</td>
                        <td className="text-right py-1.5 px-2">{fmt(Number(d.rev_7d))}</td>
                        <td className="text-right py-1.5 px-2">{fmt(Number(d.rev_30d))}</td>
                        <td className="text-right py-1.5 pl-2 font-medium">{fmt(Number(d.rev_60d))}</td>
                      </tr>
                    ))}
                    <tr className="font-bold">
                      <td className="py-2 pr-2">TOTAL</td>
                      <td className="text-right py-2 px-2">{''}</td>
                      <td className="text-right py-2 px-2">{''}</td>
                      <td className="text-right py-2 px-2">{fmt(detail.reduce((a, d) => a + Number(d.rev_7d), 0))}</td>
                      <td className="text-right py-2 px-2">{fmt(detail.reduce((a, d) => a + Number(d.rev_30d), 0))}</td>
                      <td className="text-right py-2 pl-2">{fmt(detail.reduce((a, d) => a + Number(d.rev_60d), 0))}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
            <p className="text-xs text-gray-400 px-4 pb-3">
              Includes trade-in value lines (customer escrow). Rebooked future schools appear as school products.
            </p>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400">
        Click any row for itemized purchases. Benchmarks (Jan–Jun 2026): $1,390/attendee in week one, 76% of the 60-day tail captured in 7 days, 45% club attach.
        Yellow rows = below benchmark week-one capture (execution check: fitting cart, order desk, day-3 call). Red rows = essentially
        no post-sales — investigate. Recent events' 30/60-day windows are still maturing. Club figures include trade-in value lines.
      </p>
    </div>
  );
}
